import { imin, withinRange } from '../math/fixed.ts';
import type { BattleState, BeamRules, BeamState, Enemy, Hero } from '../types.ts';
import { ev, findEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { cubBurst, cubSpawn, cubStep, cubsAlive } from './cubs.ts';
import { standBy, standDone } from './wedges.ts';

/**
 * Ruy's beam (docs/sendas/guerrero.md, chapter 5, act 3: «El balancín de Ruy»; §4 «El balancín»). Up the pass's snow
 * side, a trap Ruy built and never sprang: a great cornice of snow held up by a beam, a basket of stones for its
 * counterweight (Harl ties his old rope to it, the one cut to lower him). The beam shows a number: what the basket must
 * weigh for the cornice to come down. It starts at `start` and goes up one for each `per` of snow the wind lays on the
 * cornice, to `end`, when the wind stops. The basket has to stay under the number (at it, the cornice falls before its
 * time) and no more than `gap` under it (lighter, the beam creaks and gives: it falls by itself). Four places; stones of
 * 1, 2 and 3 at its foot: with the number going up, stones have to be changed, not only added. Cubs climb the basket and
 * throw a stone off; bands of what came through the crack come down the pass. The wind stopped: won, and the beam
 * ready if the basket stands at the number less one (the last stone of 1 goes in during the Ventisquero's fight). A
 * cornice that fell before its time falls on the empty pass: the act is won the same, without it. Lost with the hero or
 * Harl down.
 */

/** A pile of stones (its index) or a place in the basket (`PLACE` + its index). */
export const BEAM_PLACE = 10;

const fresh = (s: BattleState, r: BeamRules): BeamState => ({
  snow: 0,
  num: r.start,
  basket: new Array<number>(r.slots).fill(0),
  hand: 0,
  at: -1,
  held: 0,
  creak: 0,
  fallen: false,
  calm: false,
  next: s.tick + r.wind,
  cub: 0,
  cubs: [],
});

export const beamLoad = (w: BeamState): number => w.basket.reduce((a, b) => a + b, 0);

/** The hero sent to a pile or a place in the basket (the `mark` command). */
export const beamOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.beam;
  const w = s.senda?.beam;
  if (!r || !w || (w.calm && s.tick >= w.next)) return;
  const p = at >= BEAM_PLACE ? r.foot : r.stones[at];
  if (!p || (at >= BEAM_PLACE && at - BEAM_PLACE >= r.slots)) return;
  w.at = at;
  w.held = 0;
  orderMove(s, h, p.x, p.y + (at >= BEAM_PLACE ? 600 : 400));
  h.target = 0;
  h.forced = false;
};

export const beamCarrying = (s: BattleState): boolean => (s.senda?.beam?.hand ?? 0) > 0;

const handWork = (s: BattleState, r: BeamRules, w: BeamState): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.at < 0) return;
  const at = w.at;
  const p = at >= BEAM_PLACE ? r.foot : r.stones[at];
  if (!p || !standBy(h, w, p.x, p.y, r.reach) || !standDone(w, r.put)) return;
  if (at < BEAM_PLACE) {
    w.hand = r.values[at] ?? 0;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y, n: w.hand });
    return;
  }
  const k = at - BEAM_PLACE;
  const was = w.basket[k] as number;
  if (!w.hand && !was) return;
  w.basket[k] = w.hand;
  w.hand = was;
  ev(s, { t: 'senda', kind: 'plant', npc: '', x: p.x, y: p.y, n: k });
};

/** The cornice comes down before its time (on the empty pass): the act goes on without it. */
const fall = (s: BattleState, r: BeamRules, w: BeamState, cue: (c: string) => void): void => {
  w.fallen = true;
  ev(s, { t: 'senda', kind: 'cornice', npc: '', x: r.cornice.x, y: r.cornice.y });
  ev(s, { t: 'boom', x: r.cornice.x, y: r.cornice.y, r: 2600, kind: 'cornice' });
  cue('early');
};

/** The cubs and the bands are the act's own; the cubs it moves itself. */
export const beamHold = (s: BattleState, e: Enemy): boolean => !!s.senda?.beam?.cubs.some((c) => c.id === e.id);

export const beamTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.beam;
  const st = s.senda;
  if (!r || !st || !s.started) return;
  const w = (st.beam ??= fresh(s, r));
  handWork(s, r, w);
  // The wind stopped: the number stays; a moment to set the basket at it less one, then the act is done.
  if (w.calm) {
    if (!w.fallen && beamLoad(w) >= w.num) fall(s, r, w, cue);
    if (s.tick === w.next) {
      const ready = !w.fallen && beamLoad(w) === w.num - 1;
      st.nums.beam = ready ? 1 : 0;
      ev(s, { t: 'senda', kind: 'calm', npc: '', x: r.foot.x, y: r.foot.y, n: ready ? 1 : 2 });
      if (ready) cue('ready');
    }
    return;
  }
  // The wind lays snow on the cornice; every `per` of it the number goes up; at the end it stops.
  if (s.tick >= w.next) {
    w.snow++;
    w.next = s.tick + r.wind;
    const num = imin(r.end, r.start + Math.trunc(w.snow / r.per));
    if (num !== w.num) {
      w.num = num;
      ev(s, { t: 'senda', kind: 'num', npc: '', x: r.foot.x, y: r.foot.y, n: num });
      cue(`num${num}`);
    }
    if (w.snow >= (r.end - r.start) * r.per) {
      w.calm = true;
      w.next = s.tick + r.settle;
      ev(s, { t: 'senda', kind: 'calm', npc: '', x: r.foot.x, y: r.foot.y, n: 0 });
      cue('calm');
      return;
    }
  }
  // The window: under the number, not more than `gap` under it.
  if (!w.fallen) {
    const load = beamLoad(w);
    if (load >= w.num) fall(s, r, w, cue);
    else if (w.snow > 0 && load < w.num - r.gap) {
      if (w.creak === 0) {
        ev(s, { t: 'senda', kind: 'creak', npc: '', x: r.foot.x, y: r.foot.y });
        cue('creak');
      }
      if (++w.creak >= r.creak) fall(s, r, w, cue);
    } else w.creak = 0;
  }
  // The cubs: for the basket, up it, a stone over the side.
  const plan = r.cubs[w.cub];
  if (plan !== undefined && s.waveT >= plan) {
    w.cub++;
    cubSpawn(s, w.cubs, { x: r.top.x + ((w.cub % 3) - 1) * 900, y: r.top.y }, -2, r.cub.hp);
  }
  cubsAlive(s, w.cubs);
  for (const c of w.cubs) {
    const e = findEnemy(s, c.id);
    if (!e) continue;
    if (withinRange(e.x, e.y, r.foot.x, r.foot.y, 450)) {
      e.anim = 'attack';
      if (++c.t < r.climb) continue;
      let k = -1;
      w.basket.forEach((v, i) => {
        if (v > 0 && (k < 0 || v > (w.basket[k] as number))) k = i;
      });
      if (k >= 0 && !w.fallen) {
        ev(s, { t: 'senda', kind: 'throw', npc: '', x: r.foot.x, y: r.foot.y, n: w.basket[k] as number });
        w.basket[k] = 0;
        cue('throw');
      }
      cubBurst(s, e);
      continue;
    }
    cubStep(s, e, r.foot.x, r.foot.y, r.cub, 30);
  }
};

/** Won once the wind has stopped and the basket's last moment is gone. */
export const beamResult = (s: BattleState): 'won' | null => {
  const w = s.senda?.beam;
  return w && w.calm && s.tick > w.next + 12 ? 'won' : null;
};

/** How much snow the wind has laid, of all it lays. */
export const beamGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.beam;
  const w = s.senda?.beam;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.snow, need: (r.end - r.start) * r.per };
};
